import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Printer, GraduationCap, BookOpen } from "lucide-react";

const ACCENT = "#E07A5F";

export default function CurriculumOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api.get("/curriculum/overview")
      .then((r) => {
        setData(r.data);
        if (r.data.students?.length) setActiveId(r.data.students[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  const quarters = data?.quarters || [];
  const active = useMemo(
    () => data?.students?.find((s) => s.id === activeId) || null,
    [data, activeId]
  );

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <div className="print:hidden"><TopBar subtitle="Curriculum Overview" /></div>
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10" data-testid="curriculum-overview-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[#81B29A] mb-1">
              <GraduationCap size={18} />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">School Year {data?.school_year || ""}</span>
            </div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">Curriculum Overview</h1>
            <p className="text-[#6B7280] mt-1">Each child's subjects, lessons per quarter, and standards covered — at a glance.</p>
          </div>
          <button
            data-testid="print-curriculum-btn"
            onClick={() => window.print()}
            className="print:hidden inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold hover:opacity-90 transition-opacity"
            style={{ background: ACCENT }}>
            <Printer size={18} /> Print / Save as PDF
          </button>
        </div>

        {loading ? (
          <p className="text-[#6B7280]">Loading…</p>
        ) : !data?.students?.length ? (
          <p className="text-[#6B7280]">No students yet.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-6 print:hidden">
              {data.students.map((s) => (
                <button
                  key={s.id}
                  data-testid={`student-tab-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                    s.id === activeId
                      ? "text-white border-transparent"
                      : "bg-white text-[#3D405B] border-[#EAE6DF] hover:border-[#E07A5F]"
                  }`}
                  style={s.id === activeId ? { background: ACCENT } : {}}>
                  {s.name.split(" ")[0]}
                </button>
              ))}
            </div>

            {/* Print header per student */}
            {active && <StudentOverview student={active} quarters={quarters} />}

            {/* All students in print mode */}
            <div className="hidden print:block">
              {data.students.map((s) => (
                <div key={s.id} className="mb-10 break-inside-avoid">
                  <StudentOverview student={s} quarters={quarters} printMode />
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StudentOverview({ student, quarters, printMode }) {
  const wrapper = printMode ? "" : "block print:hidden";
  return (
    <section className={wrapper} data-testid={`student-overview-${student.id}`}>
      <div className="rounded-2xl bg-white border border-[#EAE6DF] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#EAE6DF] flex items-center justify-between">
          <div>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">{student.name}</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {student.grade_level != null && <>Grade {student.grade_level} · </>}
              {student.reading_level != null && <>Reading level {student.reading_level} · </>}
              {student.total} assignments this year
            </p>
          </div>
          <span className="stat text-3xl font-semibold text-[#81B29A]">{student.subjects.length}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#6B7280] border-b border-[#EAE6DF]">
                <th className="px-6 py-3 font-semibold">Subject</th>
                {quarters.map((q) => (
                  <th key={q.label} className="px-3 py-3 font-semibold text-center">{q.label}</th>
                ))}
                <th className="px-4 py-3 font-semibold text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {student.subjects.map((s) => (
                <tr key={s.name} data-testid={`subject-row-${s.name}`} className="border-b border-[#F1EEE8] align-top">
                  <td className="px-6 py-4">
                    <p className="font-semibold text-[#3D405B]">{s.name}</p>
                    {s.standards?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 max-w-md">
                        {s.standards.map((st) => (
                          <span key={st} className="inline-block rounded-md bg-[#F4F1EB] text-[#6B7280] text-[10px] font-medium px-1.5 py-0.5">{st}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  {quarters.map((q) => {
                    const n = s.by_quarter?.[q.label] || 0;
                    return (
                      <td key={q.label} className="px-3 py-4 text-center">
                        <span className={`stat ${n ? "text-[#3D405B]" : "text-[#CBC7BF]"}`}>{n || "—"}</span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 text-center">
                    <span className="stat font-semibold text-[#E07A5F]">{s.total}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 bg-[#FBF8F3] flex items-center gap-2 text-xs text-[#6B7280]">
          <BookOpen size={14} />
          Standards shown are from the North Carolina Standard Course of Study where available (e.g. Math).
        </div>
      </div>
    </section>
  );
}
